import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box, Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faClock,
  faEnvelope,
  faExclamation,
  faInfo,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../api/req';
import { AppContext } from '../../providers/authProvider';

function Message({ content, disabled }) {
  const [messages, setMessages] = useState([]);
  const [newMessages, setNewMessages] = useState(0);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const { auth } = useContext(AppContext);

  const onReadAllMessages = useCallback(
    async () => {
      setMessages([]);
      const r = await api.get('/api/messages/', auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const d = await r.json();
      setMessages(d.map((u) => ({
        ...u,
        show: true,
      })));
    },
    [auth],
  );

  const onReadNewMessages = useCallback(
    async () => {
      setNewMessages(0);
      const r = await api.get('/api/core/currentuser/', auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const d = await r.json();
      setNewMessages(d.results[0].messages);
    },
    [auth],
  );
  const renew = useCallback(
    () => {
      setLoading(true);
      setErr(null);
      onReadNewMessages()
        .then(onReadAllMessages)
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [onReadAllMessages, onReadNewMessages],
  );

  const deleteMessage = useCallback(
    (messageId) => {
      const loader = async () => {
        const r = await api.delete$(`/api/messages/${messageId}/`, auth);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return true;
      };
      setLoading(true);
      setErr(null);
      loader()
        .then(() => onReadNewMessages())
        .then(() => onReadAllMessages())
        .catch((e) => setErr(e.message))
        .finally(() => {
          setLoading(false);
          setOpened(false);
        });
    },
    [auth, onReadAllMessages, onReadNewMessages],
  );
  const timer = useRef();
  useEffect(
    () => {
      timer.current = setInterval(renew, 900000);
      return () => clearInterval(timer.current);
    },
    [renew],
  );

  useEffect(
    () => {
      renew();
    },
    [renew],
  );

  const currentMessage = useMemo(
    () => messages && messages.filter((i) => i.show === true)
      .reduce((R, r) => r, {}),
    [messages],
  );

  const currentMessageInfo = currentMessage.type_message === 'info';
  useEffect(
    () => {
      if (!currentMessage.id && !loading && opened) setOpened(false);
    },
    [currentMessage.id, loading, opened],
  );
  return (
    <>
      <IconButton
        disabled={disabled}
        color={err ? 'error' : 'primary'}
        title={err || content}
        onClick={() => {
          setOpened(true);
        }}
      >
        <Badge badgeContent={err ? '!' : newMessages} color={err ? 'error' : 'warning'}>
          <FontAwesomeIcon icon={faEnvelope} bounce={!!newMessages} fade={loading} />
        </Badge>
      </IconButton>
      <Dialog open={opened} onClose={() => setOpened(false)} maxWidth="lg">
        <DialogTitle sx={{ color: currentMessageInfo ? 'success.main' : 'warning.main' }}>
          { currentMessageInfo ? 'Інформація!' : 'Попередження!'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex' }}>
          <Box sx={{ color: currentMessageInfo ? 'success.main' : 'warning.main', pr: 2 }}>
            <FontAwesomeIcon icon={currentMessageInfo ? faInfo : faExclamation} size="4x" beatFade />
          </Box>
          <DialogContentText component={Box}>
            {currentMessage.text}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FontAwesomeIcon icon={faClock} />}
            variant="contained"
            color="secondary"
            onClick={() => {
              setOpened(false);
            }}
          >
            Переглянути пізніше
          </Button>
          <Button
            startIcon={<FontAwesomeIcon icon={faCheck} />}
            variant="contained"
            color="success"
            onClick={() => deleteMessage(currentMessage.id)}
          >
            Ознайомлений
          </Button>

        </DialogActions>
      </Dialog>
    </>
  );
}

Message.propTypes = {
  content: PropTypes.string,
  disabled: PropTypes.bool,
};

Message.defaultProps = {
  content: 'Повідомлення',
  disabled: false,
};

export default Message;
